import { useState } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ExpenseFilters } from '@/components/finances/ExpenseFilters';
import { ExpensesList } from '@/components/finances/ExpensesList';
import { mockExpenses, mockClients, mockProjects } from '@/mocks/data';
import { Link } from 'react-router-dom';

function ExpensesContent() {
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [recurringFilter, setRecurringFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: '',
  });

  const filteredExpenses = mockExpenses.filter((expense) => {
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    const matchesClient = clientFilter === 'all' || expense.clientId === clientFilter;
    const matchesProject = projectFilter === 'all' || expense.projectId === projectFilter;
    const matchesRecurring =
      recurringFilter === 'all' ||
      (recurringFilter === 'recurring' && expense.isRecurring) ||
      (recurringFilter === 'one-time' && !expense.isRecurring);

    let matchesDateRange = true;
    if (dateRange.from) {
      matchesDateRange = matchesDateRange && expense.date >= dateRange.from;
    }
    if (dateRange.to) {
      matchesDateRange = matchesDateRange && expense.date <= dateRange.to;
    }

    return (
      matchesCategory && matchesClient && matchesProject && matchesRecurring && matchesDateRange
    );
  });

  const totalExpenses = filteredExpenses.reduce((acc, expense) => acc + expense.amount, 0);
  const recurringExpenses = filteredExpenses.filter((e) => e.isRecurring);
  const monthlyRecurring = recurringExpenses.reduce((acc, expense) => acc + expense.amount, 0);

  // Get unique categories
  const categories = [...new Set(mockExpenses.map((e) => e.category))];

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Breadcrumb items={[{ label: 'Finances', href: '/finances' }, { label: 'Expenses' }]} />
          <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600 mt-1">Track and manage your business expenses</p>
        </div>

        <div className="flex gap-2">
          <Link
            to="/finances/recurring"
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors"
          >
            Recurring Expenses
          </Link>
          <Link
            to="/finances/expenses/new"
            className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Expense
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Expenses</p>
          <p className="text-3xl font-bold text-red-600">€{totalExpenses.toFixed(2)}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Filtered Results</p>
          <p className="text-3xl font-bold text-gray-900">{filteredExpenses.length}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Recurring</p>
          <p className="text-3xl font-bold text-orange-600">{recurringExpenses.length}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Monthly Recurring</p>
          <p className="text-3xl font-bold text-purple-600">€{monthlyRecurring.toFixed(2)}</p>
        </div>
      </div>

      {/* Filters */}
      <ExpenseFilters
        categories={categories}
        clients={mockClients}
        projects={mockProjects}
        categoryFilter={categoryFilter}
        clientFilter={clientFilter}
        projectFilter={projectFilter}
        recurringFilter={recurringFilter}
        dateRange={dateRange}
        onCategoryChange={setCategoryFilter}
        onClientChange={setClientFilter}
        onProjectChange={setProjectFilter}
        onRecurringChange={setRecurringFilter}
        onDateRangeChange={setDateRange}
      />

      {/* Expenses List */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/30">
          <h3 className="text-lg font-semibold text-gray-900">
            {filteredExpenses.length > 0 ? 'Expenses' : 'No expenses found'}
          </h3>
          {filteredExpenses.length > 0 && (
            <span className="text-sm text-gray-600">Total: €{totalExpenses.toFixed(2)}</span>
          )}
        </div>

        {filteredExpenses.length > 0 ? (
          <ExpensesList expenses={filteredExpenses} />
        ) : (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No expenses found</h3>
            <p className="text-gray-600 mb-4">
              {categoryFilter !== 'all' || clientFilter !== 'all' || projectFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Start tracking your business expenses'}
            </p>
            <Link
              to="/finances/expenses/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Your First Expense
            </Link>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default function ExpensesPage() {
  return (
    <ProtectedRoute>
      <ExpensesContent />
    </ProtectedRoute>
  );
}
